import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line, Radar } from 'react-chartjs-2';

import { companiesApi, CompanyFramework } from '../api';
import { capaApi, CapaStats } from '../../compliance/api/capa.api';
import { evidenceExpiryApi, ExpiryDashboard } from '../../compliance/api/evidenceExpiry.api';
import { trainingApi, TrainingStats } from '../../compliance/api/training.api';
import { vendorRiskApi, VendorStats } from '../../compliance/api/vendorRisk.api';
import { timelineApi, TimelineEvent } from '../../compliance/api/timeline.api';
import { riskRegisterApi } from '../../compliance/api/riskRegister.api';
import { emissionsApi } from '../../emissions/api/emissions.api';
import { certificationApi, Certification } from '../../certification/api';
import WhatsNewBanner from '@/components/WhatsNewBanner';
import { CompanyAuditPeriodPanel } from '@/domains/compliance/lifecycle/CompanyAuditPeriodPanel';
import {
  Shield, Users, FileText, AlertTriangle, CheckCircle, Clock,
  TrendingUp, RefreshCw, ChevronRight, BookOpen, Building2,
  Activity, ArrowUpRight, AlertCircle, Package, Leaf, BarChart2,
  Zap, Target, Bell,
} from 'lucide-react';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler,
);

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface DashboardStats {
  total_users: number;
  active_frameworks: number;
  total_pages: number;
  submitted_pages: number;
  approved_pages: number;
  pending_reviews: number;
  overdue_tasks: number;
}

interface RiskStats { total: number; critical: number; high: number; medium: number; low: number; }

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const EVENT_ICONS: Record<string, React.ReactNode> = {
  framework_assigned: <Shield className="w-3.5 h-3.5 text-sky-600" />,
  evidence_uploaded: <Package className="w-3.5 h-3.5 text-blue-500" />,
  evidence_approved: <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />,
  evidence_rejected: <AlertCircle className="w-3.5 h-3.5 text-red-500" />,
  certification_issued: <Target className="w-3.5 h-3.5 text-violet-500" />,
  risk_identified: <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />,
  policy_approved: <FileText className="w-3.5 h-3.5 text-blue-500" />,
  training_completed: <BookOpen className="w-3.5 h-3.5 text-teal-500" />,
  vendor_assessed: <Building2 className="w-3.5 h-3.5 text-purple-500" />,
  capa_raised: <Zap className="w-3.5 h-3.5 text-orange-500" />,
  capa_closed: <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />,
  audit_started: <Activity className="w-3.5 h-3.5 text-slate-500" />,
  default: <Bell className="w-3.5 h-3.5 text-slate-400" />,
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function lastNMonths(n: number): string[] {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (n - 1 - i));
    return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  });
}

const TOOLTIP = {
  backgroundColor: 'rgba(15,23,42,0.92)',
  titleColor: '#f8fafc', bodyColor: '#cbd5e1',
  borderColor: 'rgba(255,255,255,0.08)', borderWidth: 1,
  cornerRadius: 10, padding: 12,
};

/* ─── SVG Score Ring ─────────────────────────────────────────────────────── */

const ScoreRing: React.FC<{ score: number }> = ({ score }) => {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Needs Work';
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="140" height="140" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={circ * 0.25}
          style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)', filter: `drop-shadow(0 0 8px ${color}88)` }} />
        <text x="60" y="54" textAnchor="middle" fill="white" fontSize="22" fontWeight="900" fontFamily="system-ui, sans-serif">{score}</text>
        <text x="60" y="68" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="9" fontFamily="system-ui, sans-serif">% compliant</text>
      </svg>
      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ color, background: `${color}22`, border: `1px solid ${color}44` }}>{label}</span>
    </div>
  );
};

/* Light-background score ring */
const ScoreRingLight: React.FC<{ score: number }> = ({ score }) => {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color   = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  const track   = score >= 80 ? '#d1fae5' : score >= 60 ? '#fef3c7' : '#fee2e2';
  const txtFill = score >= 80 ? '#059669'  : score >= 60 ? '#d97706'  : '#dc2626';
  const grade   = score >= 90 ? 'A+' : score >= 80 ? 'A' : score >= 70 ? 'B' : score >= 60 ? 'C' : 'D';
  const gradeCls = score >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                 : score >= 60 ? 'bg-amber-50  text-amber-700  border-amber-200'
                 :               'bg-red-50    text-red-700    border-red-200';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <svg width="148" height="148" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke={track} strokeWidth="9" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="9"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%',
                     transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)' }} />
          <text x="60" y="52" textAnchor="middle" fill={txtFill}
                fontSize="24" fontWeight="900" fontFamily="system-ui,sans-serif">{score}</text>
          <text x="60" y="67" textAnchor="middle" fill="#94a3b8"
                fontSize="9" fontFamily="system-ui,sans-serif">% compliant</text>
        </svg>
      </div>
      <span className={`text-xs font-bold px-3 py-0.5 rounded-full border ${gradeCls}`}>
        Grade {grade}
      </span>
    </div>
  );
};

const CompanyDashboard: React.FC = () => {
  const { user } = useAuth();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [frameworks, setFrameworks] = useState<CompanyFramework[]>([]);
  const [capaStats, setCapaStats] = useState<CapaStats | null>(null);
  const [expiryStats, setExpiryStats] = useState<ExpiryDashboard | null>(null);
  const [trainingStats, setTrainingStats] = useState<TrainingStats | null>(null);
  const [vendorStats, setVendorStats] = useState<VendorStats | null>(null);
  const [riskStats, setRiskStats] = useState<RiskStats | null>(null);
  const [emissionsDash, setEmissionsDash] = useState<any>(null);
  const [recentEvents, setRecentEvents] = useState<TimelineEvent[]>([]);
  const [monthlyEvents, setMonthlyEvents] = useState<Record<string, number>>({});
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    await Promise.allSettled([
      (user?.company_id ? companiesApi.getDashboardStats(user.company_id) : Promise.resolve(null))
        .then(d => d && setStats(d)).catch(() => {}),
      companiesApi.getCompanyFrameworks().then(setFrameworks).catch(() => {}),
      capaApi.getStats().then(setCapaStats).catch(() => {}),
      evidenceExpiryApi.getDashboard().then(setExpiryStats).catch(() => {}),
      trainingApi.getStats().then(setTrainingStats).catch(() => {}),
      vendorRiskApi.getVendorStats().then(setVendorStats).catch(() => {}),
      riskRegisterApi.getStats().then(setRiskStats).catch(() => {}),
      emissionsApi.getDashboard(user?.company_id ? { company_id: user.company_id } : {})
        .then(setEmissionsDash).catch(() => {}),
      (user?.company_id ? certificationApi.getCompanyCertifications(user.company_id) : Promise.resolve([]))
        .then(setCertifications).catch(() => {}),
      timelineApi.getEvents({ limit: 10 }).then(r => setRecentEvents(r.events || [])).catch(() => {}),
      timelineApi.getEvents({ date_from: sixMonthsAgo.toISOString().split('T')[0], limit: 200 }).then(r => {
        const b: Record<string, number> = {};
        (r.events || []).forEach((e: TimelineEvent) => {
          const k = new Date(e.event_at).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
          b[k] = (b[k] || 0) + 1;
        });
        setMonthlyEvents(b);
      }).catch(() => {}),
    ]);

    setLastRefreshed(new Date());
    setLoading(false);
  }, [user?.company_id]);

  useEffect(() => { load(); }, [load]);

  /* ─── Derived ──────────────────────────────────────────────────────────── */

  const complianceScore = frameworks.length > 0
    ? Math.round(frameworks.reduce((acc, f) =>
        acc + (f.total_pages > 0 ? (f.approved_count / f.total_pages) * 100 : 0), 0) / frameworks.length)
    : 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // Emissions data — scope totals for hero section
  const emScope1 = Number(emissionsDash?.emissions?.scope1_tco2e ?? 0);
  const emScope2 = Number(emissionsDash?.emissions?.scope2_tco2e ?? 0);
  const emScope3 = Number(emissionsDash?.emissions?.scope3_tco2e ?? 0);
  const emTotal = Number(emissionsDash?.emissions?.total_tco2e ?? 0);
  const emNet = Number(emissionsDash?.net_emissions_tco2e ?? emTotal);
  const emOffsets = Number(emissionsDash?.offsets?.total_tco2e ?? 0);
  const emTargets = emissionsDash?.targets ?? { total: 0, achieved: 0 };
  const emBulk = emissionsDash?.bulk_sessions ?? { total: 0, completed: 0, total_rows: 0 };
  const hasEmissions = emTotal > 0 || emBulk.total > 0;

  const criticalAlerts = [
    stats?.overdue_tasks && stats.overdue_tasks > 0
      ? { label: `${stats.overdue_tasks} overdue task${stats.overdue_tasks !== 1 ? 's' : ''}`, color: 'bg-red-50 text-red-700 border-red-200', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
      : null,
    expiryStats?.expiring_7_days && expiryStats.expiring_7_days > 0
      ? { label: `${expiryStats.expiring_7_days} evidence expiring soon`, color: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock className="w-3.5 h-3.5" /> }
      : null,
    capaStats?.overdue && capaStats.overdue > 0
      ? { label: `${capaStats.overdue} CAPA overdue`, color: 'bg-orange-50 text-orange-700 border-orange-200', icon: <Zap className="w-3.5 h-3.5" /> }
      : null,
    riskStats?.critical && riskStats.critical > 0
      ? { label: `${riskStats.critical} critical risk${riskStats.critical !== 1 ? 's' : ''}`, color: 'bg-red-50 text-red-700 border-red-200', icon: <Shield className="w-3.5 h-3.5" /> }
      : null,
    emTotal > 0 && emTargets.total === 0
      ? { label: 'No emission targets set', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <Leaf className="w-3.5 h-3.5" /> }
      : null,
  ].filter(Boolean) as { label: string; color: string; icon: React.ReactNode }[];

  /* ─── Chart data ───────────────────────────────────────────────────────── */

  const fwLabels = frameworks.map(f => f.name.length > 16 ? f.name.slice(0, 14) + '…' : f.name);
  const frameworkChartData = {
    labels: fwLabels,
    datasets: [
      { label: 'Approved', data: frameworks.map(f => f.total_pages > 0 ? Math.round((f.approved_count / f.total_pages) * 100) : 0), backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: 5, borderSkipped: false },
      { label: 'In Progress', data: frameworks.map(f => f.total_pages > 0 ? Math.round(((f.in_progress_count + f.submitted_count) / f.total_pages) * 100) : 0), backgroundColor: 'rgba(59,130,246,0.75)', borderRadius: 5, borderSkipped: false },
      { label: 'Pending', data: frameworks.map(f => f.total_pages > 0 ? Math.round((f.not_started_count / f.total_pages) * 100) : 0), backgroundColor: 'rgba(226,232,240,0.7)', borderRadius: 5, borderSkipped: false },
    ],
  };

  const capaDoughnut = {
    labels: ['Open', 'In Progress', 'Resolved', 'Overdue'],
    datasets: [{ data: capaStats ? [capaStats.open, capaStats.in_progress, capaStats.resolved, capaStats.overdue] : [0,0,0,0], backgroundColor: ['#f59e0b','#3b82f6','#10b981','#ef4444'], borderWidth: 0, hoverOffset: 8 }],
  };

  const total = riskStats?.total || 1;
  const riskRadar = {
    labels: ['Critical', 'High', 'Medium', 'Low', 'Open'],
    datasets: [{ label: 'Risk %', data: riskStats ? [
      Math.round((riskStats.critical / total) * 100),
      Math.round((riskStats.high / total) * 100),
      Math.round((riskStats.medium / total) * 100),
      Math.round((riskStats.low / total) * 100),
      Math.round(((riskStats.critical + riskStats.high) / total) * 100),
    ] : [0,0,0,0,0], backgroundColor: 'rgba(239,68,68,0.12)', borderColor: '#ef4444', borderWidth: 2, pointBackgroundColor: '#ef4444', pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 5 }],
  };

  const monthLabels = lastNMonths(6);
  const complianceTrendData = {
    labels: monthLabels,
    datasets: [{ label: 'Compliance Activity', data: monthLabels.map(m => monthlyEvents[m] || 0), borderColor: '#0EA5E9', backgroundColor: 'rgba(14,165,233,0.12)', borderWidth: 2.5, fill: true, tension: 0.45, pointBackgroundColor: '#0EA5E9', pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 5, pointHoverRadius: 7 }],
  };

  // Category breakdown chart
  const emCats: any[] = emissionsDash?.breakdown?.byCategory?.slice(0, 8) || [];
  // Fall back to scope breakdown if no categories
  const emScopeBreakdown: any[] = emissionsDash?.breakdown?.byScope || [];
  const displayCats = emCats.length > 0 ? emCats : emScopeBreakdown.filter((s: any) => Number(s.total_co2e) > 0);
  const emissionsBarData = {
    labels: displayCats.map((c: any) => {
      const name = c.category ?? c.scope ?? '?';
      return name.length > 22 ? name.slice(0, 20) + '…' : name;
    }),
    datasets: [{ label: 'tCO₂e', data: displayCats.map((c: any) => Number(Number(c.total_co2e || 0).toFixed(2))), backgroundColor: ['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#8b5cf6','#ec4899'], borderRadius: 6, borderSkipped: false }],
  };

  const emMonthly: any[] = emissionsDash?.trends?.monthly?.slice(-6) || [];
  const emTrendData = {
    labels: emMonthly.map((m: any) => new Date(m.date).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })),
    datasets: [{ label: 'tCO₂e', data: emMonthly.map((m: any) => Number(Number(m.total_co2e || 0).toFixed(2))), borderColor: '#34d399', backgroundColor: 'rgba(52,211,153,0.1)', borderWidth: 2.5, fill: true, tension: 0.45, pointBackgroundColor: '#34d399', pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 4 }],
  };

  const trainingBarData = {
    labels: ['Passed', 'Failed', 'Overdue', 'In Progress'],
    datasets: [{ label: 'Assignments', data: trainingStats ? [trainingStats.passed, trainingStats.failed, trainingStats.overdue, trainingStats.total_assignments - trainingStats.completed] : [0,0,0,0], backgroundColor: ['#10b981','#ef4444','#f59e0b','#3b82f6'], borderRadius: 7, borderSkipped: false }],
  };

  /* ─── Chart options ────────────────────────────────────────────────────── */

  const stackedBar = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'top' as const, labels: { usePointStyle: true, padding: 18, font: { size: 12 }, color: '#64748b' } }, tooltip: { ...TOOLTIP } },
    scales: { x: { stacked: true, grid: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8' } }, y: { stacked: true, max: 100, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: (v: any) => v + '%', font: { size: 11 }, color: '#94a3b8' } } },
  };

  const doughnutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '68%',
    plugins: { legend: { display: false }, tooltip: { ...TOOLTIP } },
  };

  const lineOpts = (yLabel: string) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { ...TOOLTIP } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8' } }, y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 11 }, color: '#94a3b8' }, title: { display: true, text: yLabel, font: { size: 11 }, color: '#94a3b8' } } },
  });

  const barOpts = (yLabel: string) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { ...TOOLTIP } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8' } }, y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 11 }, color: '#94a3b8' }, title: { display: true, text: yLabel, font: { size: 11 }, color: '#94a3b8' } } },
  });

  const radarOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { ...TOOLTIP } },
    scales: { r: { min: 0, max: 100, grid: { color: 'rgba(0,0,0,0.06)' }, angleLines: { color: 'rgba(0,0,0,0.06)' }, pointLabels: { font: { size: 12 }, color: '#475569' }, ticks: { display: false, stepSize: 20 } } },
  };

  /* ─── Loading ──────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  /* ─── Render ───────────────────────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ══════════════════════════════════════════════════════════════════════
          HERO — compact command centre
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-5">

          {/* ── Top bar: greeting + refresh ─────────────────────────────────── */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sky-600 text-[11px] font-bold tracking-widest uppercase leading-none mb-1">
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <h1 className="text-xl font-bold text-slate-900 leading-tight">
                {greeting}, {user?.first_name} 👋
                <span className="ml-2 text-sm font-normal text-slate-400">— your compliance command centre</span>
              </h1>
            </div>
            <button onClick={load} className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" />
              <span className="text-[11px] text-slate-300 ml-2">
                {lastRefreshed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          </div>

          {/* ── Main row: score ring | health pills | ring cards ────────────── */}
          <div className="flex items-stretch gap-4">

            {/* Score ring — compact */}
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-1.5 pr-4 border-r border-slate-100">
              <ScoreRingLight score={complianceScore} />
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Health</p>
            </div>

            {/* Health pills — vertical stack */}
            <div className="flex flex-col justify-center gap-1.5 pr-4 border-r border-slate-100 flex-shrink-0">
              {[
                { label: 'Compliance', value: `${complianceScore}%`, icon: <Shield className="w-3 h-3" />, color: complianceScore >= 80 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : complianceScore >= 60 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200' },
                { label: 'Training', value: trainingStats ? `${trainingStats.completion_rate}%` : '—', icon: <BookOpen className="w-3 h-3" />, color: (trainingStats?.completion_rate ?? 0) >= 80 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : 'text-amber-600 bg-amber-50 border-amber-200' },
                { label: 'Risks', value: riskStats ? String(riskStats.total) : '—', icon: <AlertTriangle className="w-3 h-3" />, color: (riskStats?.critical ?? 0) > 0 ? 'text-red-600 bg-red-50 border-red-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200' },
                { label: 'Vendors', value: vendorStats ? String(vendorStats.total) : '—', icon: <Building2 className="w-3 h-3" />, color: ((vendorStats?.critical_risk ?? 0) + (vendorStats?.high_risk ?? 0)) > 0 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200' },
              ].map(chip => (
                <div key={chip.label} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${chip.color}`}>
                  {chip.icon}
                  <span className="text-[10px] text-slate-500 font-medium w-14">{chip.label}</span>
                  <span className="font-bold">{chip.value}</span>
                </div>
              ))}
            </div>

            {/* Ring cards + Emissions card */}
            <div className="flex-1 grid grid-cols-4 gap-3 min-w-0">
              <RingCard
                title="Pending Reviews"
                centerValue={stats?.pending_reviews ?? 0}
                ringPct={stats && stats.total_pages > 0 ? Math.round((stats.submitted_pages / stats.total_pages) * 100) : 0}
                color="#f59e0b" track="#fef3c7"
                legend={[
                  { label: `${stats?.submitted_pages ?? 0} submitted`, dot: '#f59e0b' },
                  { label: `${stats?.approved_pages ?? 0} approved`, dot: '#10b981' },
                ]}
                stats={[
                  { label: 'Total pages', value: stats?.total_pages ?? '—' },
                  { label: 'Frameworks', value: frameworks.length },
                ]}
              />
              <RingCard
                title="Overdue Tasks"
                centerValue={stats?.overdue_tasks ?? 0}
                ringPct={capaStats && capaStats.total > 0 ? Math.round((capaStats.overdue / capaStats.total) * 100) : (stats?.overdue_tasks ? 60 : 0)}
                color="#ef4444" track="#fee2e2"
                legend={[
                  { label: `${capaStats?.overdue ?? 0} CAPA overdue`, dot: '#ef4444' },
                  { label: `${expiryStats?.expiring_7_days ?? 0} expiring`, dot: '#f97316' },
                ]}
                stats={[
                  { label: 'Open CAPAs', value: capaStats?.open ?? '—' },
                  { label: 'Expiring 30d', value: expiryStats?.expiring_30_days ?? '—' },
                ]}
              />
              <RingCard
                title="Team Members"
                centerValue={stats?.total_users ?? 0}
                ringPct={complianceScore}
                color="#0369A1" track="#e0f2fe"
                legend={[
                  { label: `${frameworks.length} framework${frameworks.length !== 1 ? 's' : ''}`, dot: '#0369A1' },
                  { label: `${trainingStats?.completion_rate ?? 0}% trained`, dot: '#14b8a6' },
                ]}
                stats={[
                  { label: 'Score', value: `${complianceScore}%` },
                  { label: 'Avg score', value: trainingStats ? `${trainingStats.average_score}%` : '—' },
                ]}
              />

              {/* Emissions ring card */}
              <div className="bg-gradient-to-br from-emerald-50/80 to-teal-50/60 border border-emerald-200 rounded-2xl p-4 shadow-sm flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Leaf className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Carbon</p>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <div className="text-xl font-black text-slate-800 leading-none">
                    {emTotal > 0 ? emTotal.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '0'}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">tCO₂e total ({emissionsDash?.period ?? new Date().getFullYear()})</div>
                </div>
                <div className="mt-2 space-y-1">
                  {[
                    { label: 'Scope 1', value: emScope1, color: 'bg-blue-500' },
                    { label: 'Scope 2', value: emScope2, color: 'bg-violet-500' },
                    { label: 'Scope 3', value: emScope3, color: 'bg-emerald-500' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.color}`} />
                      <span className="text-[10px] text-slate-500 flex-1">{s.label}</span>
                      <span className="text-[10px] font-bold text-slate-600">{s.value > 0 ? s.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</span>
                    </div>
                  ))}
                </div>
                {emTotal > 0 && (
                  <div className="mt-2 flex gap-0.5 h-1.5 rounded-full overflow-hidden bg-slate-100">
                    {emScope1 > 0 && <div className="bg-blue-500 rounded-l-full" style={{ width: `${(emScope1 / emTotal) * 100}%` }} />}
                    {emScope2 > 0 && <div className="bg-violet-500" style={{ width: `${(emScope2 / emTotal) * 100}%` }} />}
                    {emScope3 > 0 && <div className="bg-emerald-500 rounded-r-full" style={{ width: `${(emScope3 / emTotal) * 100}%` }} />}
                  </div>
                )}
                {emOffsets > 0 && (
                  <div className="mt-1.5 flex items-center gap-1">
                    <span className="text-[9px] text-teal-600 font-semibold">Net: {emNet.toLocaleString(undefined, { maximumFractionDigits: 1 })}t</span>
                    <span className="text-[9px] text-teal-500">(-{emOffsets.toLocaleString(undefined, { maximumFractionDigits: 1 })} offsets)</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Page progress bar + alerts — thin strip ─────────────────────── */}
          {(stats?.total_pages || criticalAlerts.length > 0) && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4">
              {stats && stats.total_pages > 0 && (
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap">Pages</span>
                  <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-slate-100 flex-1 max-w-xs">
                    <div className="bg-emerald-500 transition-all" style={{ width: `${(stats.approved_pages / stats.total_pages) * 100}%` }} />
                    <div className="bg-blue-400 transition-all"    style={{ width: `${(stats.submitted_pages / stats.total_pages) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-emerald-600 font-semibold whitespace-nowrap">{stats.approved_pages} approved</span>
                  <span className="text-[10px] text-blue-500 font-semibold whitespace-nowrap">{stats.submitted_pages} submitted</span>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{stats.total_pages - stats.approved_pages - stats.submitted_pages} remaining</span>
                </div>
              )}
              {criticalAlerts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 ml-auto">
                  {criticalAlerts.map(a => (
                    <span key={a.label} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${a.color}`}>
                      {a.icon} {a.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">

        {/* ── What's New Banner ────────────────────────────────────────── */}
        <WhatsNewBanner />

        {/* ── External Audit Panel (Phase 2 / Phase 5) ─────────────────────── */}
        <CompanyAuditPeriodPanel
          assignments={frameworks.map((f: any) => ({
            id: f.assignment_id ?? f.id,
            framework_id: f.framework_id ?? f.id,
            framework_name: f.framework_name ?? f.name ?? 'Framework',
            framework_version: f.framework_version ?? f.version ?? '',
          }))}
        />

        {/* ── Section 1: Framework compliance (full width) ─────────────────── */}
        <Section title="Framework Compliance Breakdown" subtitle="Approved · In Progress · Pending per framework" icon={<BarChart2 className="w-4 h-4 text-sky-600" />} href="/company/frameworks" accentColor="indigo">
          {frameworks.length === 0 ? (
            <Empty message="No frameworks assigned yet" sub="Your compliance partner will assign frameworks to your company. You will be notified when this happens." />
          ) : (
            <div style={{ height: 240 }}>
              <Bar data={frameworkChartData} options={stackedBar} />
            </div>
          )}
        </Section>

        {/* ── Section 2: Compliance trend + Evidence expiry ────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <Section title="Compliance Activity Trend" subtitle="Events recorded across all modules — last 6 months" icon={<TrendingUp className="w-4 h-4 text-violet-500" />} accentColor="violet">
              <div style={{ height: 220 }}>
                <Line data={complianceTrendData} options={lineOpts('Events')} />
              </div>
            </Section>
          </div>
          <div>
            <Section title="Evidence Expiry" subtitle="Time-sensitive evidence items" icon={<FileText className="w-4 h-4 text-amber-500" />} href="/compliance/evidence-expiry" accentColor="amber">
              {expiryStats ? (
                <div className="space-y-2">
                  {[
                    { label: 'Expired', value: expiryStats.expired, tier: 'critical' },
                    { label: 'Expires in 7 days', value: expiryStats.expiring_7_days, tier: 'high' },
                    { label: 'Expires in 30 days', value: expiryStats.expiring_30_days, tier: 'medium' },
                    { label: 'Renewal tasks pending', value: expiryStats.renewal_tasks_pending, tier: 'info' },
                  ].map(r => {
                    const styles: Record<string, string> = {
                      critical: 'bg-red-50 border border-red-200 text-red-700',
                      high: 'bg-orange-50 border border-orange-200 text-orange-700',
                      medium: 'bg-amber-50 border border-amber-200 text-amber-700',
                      info: 'bg-blue-50 border border-blue-200 text-blue-700',
                    };
                    return (
                      <div key={r.label} className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold ${styles[r.tier]}`}>
                        <span>{r.label}</span>
                        <span className="text-base font-bold">{r.value}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <Empty message="Loading expiry data…" />}
            </Section>
          </div>
        </div>

        {/* ── Section 3: CAPA + Risk ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          <Section title="CAPA Pipeline" subtitle="Corrective & preventive action status" icon={<AlertCircle className="w-4 h-4 text-amber-500" />} href="/compliance/capa" accentColor="amber" badge={capaStats?.overdue ? { label: `${capaStats.overdue} overdue`, color: 'bg-red-100 text-red-700' } : undefined}>
            {capaStats && capaStats.total > 0 ? (
              <div className="flex items-center gap-5">
                <div className="relative flex-shrink-0" style={{ width: 180, height: 180 }}>
                  <Doughnut data={capaDoughnut} options={doughnutOpts} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold text-slate-800">{capaStats.total}</span>
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Total</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2.5">
                  {[
                    { label: 'Open', value: capaStats.open, dot: 'bg-amber-400', text: 'text-amber-700' },
                    { label: 'In Progress', value: capaStats.in_progress, dot: 'bg-blue-400', text: 'text-blue-700' },
                    { label: 'Resolved', value: capaStats.resolved, dot: 'bg-emerald-400', text: 'text-emerald-700' },
                    { label: 'Overdue', value: capaStats.overdue, dot: 'bg-red-500', text: 'text-red-700' },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-2.5">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${r.dot}`} />
                      <span className="text-sm text-slate-600 flex-1">{r.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${r.dot} rounded-full`} style={{ width: `${capaStats.total > 0 ? (r.value / capaStats.total) * 100 : 0}%` }} />
                        </div>
                        <span className={`text-sm font-bold w-6 text-right ${r.text}`}>{r.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <Empty message="No CAPA records yet" sub="Corrective actions will appear here when raised during compliance reviews." actionLabel="View CAPA" actionHref="/compliance/capa" />}
          </Section>

          <Section title="Risk Profile" subtitle="Severity distribution across all risk categories" icon={<AlertTriangle className="w-4 h-4 text-red-500" />} href="/compliance/risk-register" accentColor="red" badge={riskStats?.critical ? { label: `${riskStats.critical} critical`, color: 'bg-red-100 text-red-700' } : undefined}>
            {riskStats && riskStats.total > 0 ? (
              <div className="flex items-center gap-4">
                <div style={{ height: 220, flex: 1 }}>
                  <Radar data={riskRadar} options={radarOpts} />
                </div>
                <div className="space-y-2 flex-shrink-0">
                  {[
                    { label: 'Critical', value: riskStats.critical, bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
                    { label: 'High', value: riskStats.high, bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700' },
                    { label: 'Medium', value: riskStats.medium, bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
                    { label: 'Low', value: riskStats.low, bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
                  ].map(r => (
                    <div key={r.label} className={`flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg border text-xs font-semibold ${r.bg} ${r.text}`}>
                      <span>{r.label}</span>
                      <span className="font-bold text-sm">{r.value}</span>
                    </div>
                  ))}
                  <div className="text-center pt-1 border-t border-slate-100">
                    <span className="text-xs text-slate-400">{riskStats.total} total risks</span>
                  </div>
                </div>
              </div>
            ) : <Empty message="No risks recorded yet" sub="Start tracking risks in your register to visualise your risk profile." actionLabel="Add Risk" actionHref="/compliance/risk-register" />}
          </Section>
        </div>

        {/* ── Section 4: Emissions charts ─────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Emissions by Category" subtitle="Top carbon sources (tCO₂e)" icon={<Leaf className="w-4 h-4 text-emerald-500" />} href="/emissions/scopes" accentColor="emerald"
            badge={emBulk.total > 0 ? { label: `${emBulk.total} bulk session${emBulk.total !== 1 ? 's' : ''}`, color: 'bg-emerald-100 text-emerald-700' } : undefined}>
            {displayCats.length > 0 ? (
              <div style={{ height: 240 }}>
                <Bar data={emissionsBarData} options={{ ...barOpts('tCO₂e'), indexAxis: 'y' as const, plugins: { ...barOpts('tCO₂e').plugins, tooltip: { ...TOOLTIP, callbacks: { label: (ctx: any) => ` ${ctx.parsed.x} tCO₂e` } } } }} />
              </div>
            ) : <Empty message="No emissions data yet" sub="Import data via Excel Import, use the Bulk Calculator, or log individual calculations." actionLabel="Go to Emissions" actionHref="/emissions/scopes" />}
          </Section>

          <Section title="Emissions Monthly Trend" subtitle="Total GHG output — last 6 months" icon={<TrendingUp className="w-4 h-4 text-emerald-500" />} accentColor="emerald"
            badge={emTargets.total > 0 ? { label: `${emTargets.achieved}/${emTargets.total} targets`, color: 'bg-teal-100 text-teal-700' } : undefined}>
            {emMonthly.length > 0 ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-2xl font-bold text-emerald-600">
                    {emMonthly.reduce((s: number, m: any) => s + Number(m.total_co2e || 0), 0).toFixed(1)}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-700">tCO₂e total</div>
                    <div className="text-[10px] text-slate-400">last 6 months</div>
                  </div>
                  {emOffsets > 0 && (
                    <div className="ml-auto text-right">
                      <div className="text-sm font-bold text-teal-600">-{emOffsets.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
                      <div className="text-[10px] text-teal-500">tCO₂e offsets</div>
                    </div>
                  )}
                </div>
                <div style={{ height: 180 }}>
                  <Line data={emTrendData} options={lineOpts('tCO₂e')} />
                </div>
              </>
            ) : hasEmissions ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Your emissions total <span className="font-bold text-slate-700">{emTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} tCO₂e</span> for {emissionsDash?.period ?? new Date().getFullYear()}.</p>
                <p className="text-xs text-slate-400">Monthly trend will appear as calculations span multiple months.</p>
                <div className="flex gap-2 mt-2">
                  <Link to="/emissions/bulk-calculator" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Bulk Calculator &rarr;</Link>
                  <Link to="/emissions/excel-import" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Excel Import &rarr;</Link>
                </div>
              </div>
            ) : <Empty message="No monthly emissions data yet" sub="Start tracking emissions to see trends over time." actionLabel="Go to Emissions" actionHref="/emissions/scopes" />}
          </Section>
        </div>

        {/* ── Section 5: Training + Vendor Risk ───────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          <Section title="Training Status" subtitle="Assignment outcomes across all staff" icon={<BookOpen className="w-4 h-4 text-blue-500" />} href="/compliance/training" accentColor="blue" badge={trainingStats?.overdue ? { label: `${trainingStats.overdue} overdue`, color: 'bg-amber-100 text-amber-700' } : undefined}>
            {trainingStats && trainingStats.total_assignments > 0 ? (
              <div className="flex items-center gap-5">
                <div style={{ height: 200, flex: 1 }}>
                  <Bar data={trainingBarData} options={barOpts('Assignments')} />
                </div>
                <div className="flex-shrink-0 text-center space-y-3">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{trainingStats.completion_rate}%</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mt-0.5">Completion</div>
                  </div>
                  <div className="w-px h-6 bg-slate-100 mx-auto" />
                  <div>
                    <div className="text-2xl font-bold text-slate-700">{trainingStats.average_score}%</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mt-0.5">Avg Score</div>
                  </div>
                </div>
              </div>
            ) : <Empty message="No training assignments yet" sub="Training modules will appear when assigned by your admin." actionLabel="View Training" actionHref="/compliance/training" />}
          </Section>

          <Section title="Vendor Risk Overview" subtitle="Third-party risk exposure summary" icon={<Building2 className="w-4 h-4 text-purple-500" />} href="/compliance/vendor-risk" accentColor="purple" badge={vendorStats && (vendorStats.high_risk + vendorStats.critical_risk) > 0 ? { label: `${vendorStats.high_risk + vendorStats.critical_risk} high/critical`, color: 'bg-red-100 text-red-700' } : undefined}>
            {vendorStats && vendorStats.total > 0 ? (
              <div className="space-y-1">
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-3xl font-bold text-slate-900">{vendorStats.total}</span>
                  <span className="text-sm text-slate-500 mb-1">vendors total</span>
                </div>
                {[
                  { label: 'Active', value: vendorStats.active, color: 'bg-emerald-500' },
                  { label: 'Under Review', value: vendorStats.under_review, color: 'bg-amber-400' },
                  { label: 'High / Critical Risk', value: vendorStats.high_risk + vendorStats.critical_risk, color: 'bg-red-500' },
                  { label: 'Due for Review', value: vendorStats.due_for_review, color: 'bg-purple-500' },
                ].map(r => (
                  <div key={r.label}>
                    <div className="flex justify-between text-xs text-slate-600 mb-1 font-medium">
                      <span>{r.label}</span>
                      <span className="font-bold text-slate-800">{r.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                      <div className={`h-full ${r.color} rounded-full transition-all`} style={{ width: `${Math.round((r.value / vendorStats.total) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty message="No vendors registered yet" sub="Register third-party vendors for risk tracking and compliance." actionLabel="Add Vendor" actionHref="/compliance/vendor-risk" />}
          </Section>
        </div>

        {/* ── Section 6: Framework progress + Activity + Quick actions ─────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Framework mini-progress */}
          <Section title="Framework Progress" subtitle="Per-framework approval rate" icon={<Activity className="w-4 h-4 text-slate-400" />} href="/company/frameworks" accentColor="slate">
            {frameworks.length === 0 ? <Empty message="No frameworks assigned" /> : (
              <div className="space-y-3.5">
                {frameworks.map(f => {
                  const pct = f.total_pages > 0 ? Math.round((f.approved_count / f.total_pages) * 100) : 0;
                  const inProg = f.total_pages > 0 ? Math.round(((f.in_progress_count + f.submitted_count) / f.total_pages) * 100) : 0;
                  const scoreColor = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500';
                  return (
                    <div key={f.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-slate-700 truncate max-w-[160px]">{f.name}</span>
                        <span className={`text-xs font-bold ${scoreColor}`}>{pct}%</span>
                      </div>
                      <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-slate-100">
                        <div className="bg-emerald-500 rounded-l-full" style={{ width: `${pct}%` }} />
                        <div className="bg-blue-300" style={{ width: `${inProg}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Activity feed */}
          <div className="lg:col-span-2">
            <Section title="Recent Activity" subtitle="Latest events across all compliance modules" icon={<Clock className="w-4 h-4 text-slate-400" />} href="/compliance/timeline" accentColor="slate">
              {recentEvents.length === 0 ? (
                <Empty message="No activity recorded yet" sub="Activity will appear as your team works through compliance pages." />
              ) : (
                <div className="space-y-0">
                  {recentEvents.map((e, idx) => (
                    <div key={e.id} className="flex gap-3 group">
                      {/* Timeline line */}
                      <div className="flex flex-col items-center flex-shrink-0 w-8">
                        <div className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5">
                          {EVENT_ICONS[e.event_type] || EVENT_ICONS.default}
                        </div>
                        {idx < recentEvents.length - 1 && (
                          <div className="w-px flex-1 bg-slate-100 my-1" style={{ minHeight: 12 }} />
                        )}
                      </div>
                      <div className="flex-1 pb-3 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate leading-snug">{e.event_title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{timeAgo(e.event_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* ── Quick Actions ────────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4 text-sky-600" /> Quick Actions
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ...(certifications.length > 0 ? [{ label: 'Certifications', href: '/company/certifications', icon: <Target className="w-5 h-5" />, color: 'text-emerald-700 bg-emerald-50 group-hover:bg-emerald-100', border: 'hover:border-emerald-300' }] : []),
              { label: 'Compliance Pages', href: '/company/compliance-pages', icon: <CheckCircle className="w-5 h-5" />, color: 'text-emerald-600 bg-emerald-50 group-hover:bg-emerald-100', border: 'hover:border-emerald-300' },
              { label: 'Emissions', href: '/emissions/scopes', icon: <Leaf className="w-5 h-5" />, color: 'text-emerald-600 bg-emerald-50 group-hover:bg-emerald-100', border: 'hover:border-emerald-300' },
              { label: 'Risk Register', href: '/compliance/risk-register', icon: <AlertTriangle className="w-5 h-5" />, color: 'text-amber-600 bg-amber-50 group-hover:bg-amber-100', border: 'hover:border-amber-300' },
              { label: 'Policies', href: '/compliance/policies', icon: <FileText className="w-5 h-5" />, color: 'text-blue-600 bg-blue-50 group-hover:bg-blue-100', border: 'hover:border-blue-300' },
              { label: 'Controls', href: '/compliance/controls', icon: <Shield className="w-5 h-5" />, color: 'text-sky-700 bg-sky-50 group-hover:bg-indigo-100', border: 'hover:border-indigo-300' },
              { label: 'Reports', href: '/compliance/management-report', icon: <ArrowUpRight className="w-5 h-5" />, color: 'text-teal-600 bg-teal-50 group-hover:bg-teal-100', border: 'hover:border-teal-300' },
            ].map(a => (
              <Link key={a.href} to={a.href} className={`group flex flex-col items-center gap-2.5 p-4 rounded-xl border border-slate-100 ${a.border} transition-all hover:shadow-sm`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${a.color}`}>
                  {a.icon}
                </div>
                <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800 text-center leading-tight transition-colors">{a.label}</span>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─── Shared sub-components ──────────────────────────────────────────────── */

/* ── Breakdown-style ring card (inspired by Evidence Library breakdown widget) */
const RingCard: React.FC<{
  title: string;
  centerValue: React.ReactNode;
  ringPct: number;
  color: string;
  track: string;
  legend: { label: string; dot: string }[];
  stats: { label: string; value: React.ReactNode }[];
}> = ({ title, centerValue, ringPct, color, track, legend, stats }) => {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, ringPct)) / 100) * circ;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
      {/* Donut ring */}
      <div className="flex-shrink-0 relative" style={{ width: 80, height: 80 }}>
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={r} fill="none" stroke={track} strokeWidth="8" />
          <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%',
                     transition: 'stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[17px] font-bold text-slate-800 leading-none">{centerValue}</span>
        </div>
      </div>

      {/* Right content */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{title}</p>
        <div className="space-y-1 mb-2.5">
          {legend.map(l => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: l.dot }} />
              <span className="text-xs text-slate-600">{l.label}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-2 space-y-1">
          {stats.map(s => (
            <div key={s.label} className="flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{s.label}</span>
              <span className="text-[11px] font-bold text-slate-600">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{
  title: string; subtitle: string; icon: React.ReactNode; href?: string;
  accentColor?: string; badge?: { label: string; color: string };
  children: React.ReactNode;
}> = ({ title, subtitle, icon, href, badge, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 bg-slate-50 rounded-lg">{icon}</div>
        <div>
          <h2 className="font-bold text-slate-800 text-sm leading-tight">{title}</h2>
          <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {badge && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
        )}
        {href && (
          <Link to={href} className="flex items-center gap-0.5 text-xs text-sky-700 hover:text-indigo-800 font-medium transition-colors">
            View <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
    {children}
  </div>
);

const Empty: React.FC<{ message: string; sub?: string; actionLabel?: string; actionHref?: string }> = ({ message, sub, actionLabel, actionHref }) => (
  <div className="flex flex-col items-center justify-center h-40 text-center">
    <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mb-3">
      <BarChart2 className="w-6 h-6 text-slate-200" />
    </div>
    <p className="text-sm font-medium text-slate-400">{message}</p>
    {sub && <p className="text-xs text-slate-300 mt-1 max-w-xs leading-relaxed">{sub}</p>}
    {actionLabel && actionHref && (
      <Link to={actionHref} className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700">{actionLabel} &rarr;</Link>
    )}
  </div>
);

export default CompanyDashboard;
